import React, { useEffect, useRef } from 'react';

import useEventTracking from '@hooks/useEventTracking';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { TRINITY_PLAYER_API_KEY } from '@src/env';
import { ClickTarget, ImpressionElement, TrackingLocation, click, impression } from '@tracking';

export type TrinityAudioEventName =
  | 'injectorImp'
  | 'playerReady'
  | 'playClicked'
  | 'pauseClicked'
  | 'resumed'
  | 'contentStarted'
  | 'onFirstQuartile'
  | 'onMidPoint'
  | 'onThirdQuartile'
  | 'onComplete'
  | 'adOpp'
  | 'onAdStarted'
  | 'onAdComplete'
  | 'enteredView'
  | 'exitedView'
  | 'FABShow'
  | 'FABHide'
  | 'touchStart'
  | 'touchEnd'
  | 'scrubbing'
  | 'translateTo';

export type TrinityAudioEventDetail = {
  /** Resolved name when it maps to `TrinityAudioEventName`; otherwise the raw `action` string. */
  event: TrinityAudioEventName | string;
  action: string;
  playerId: string;
  message?: unknown;
};

type TrinityPlayerMessageMap = Partial<Record<TrinityAudioEventName, string>>;

type TrinityPlayerGlobal = {
  constants: { postMessageType: string };
  message: TrinityPlayerMessageMap;
};

export function getTrinityPlayer(): TrinityPlayerGlobal | undefined {
  return (window as Window & { TRINITY_PLAYER?: TrinityPlayerGlobal }).TRINITY_PLAYER;
}

function resolveEventName(
  action: string,
  msg: TrinityPlayerMessageMap | undefined
): TrinityAudioEventName | string {
  if (action === 'injectorImp') return 'injectorImp';
  if (!msg) return action;
  for (const [key, val] of Object.entries(msg) as [TrinityAudioEventName, string | undefined][]) {
    if (val === action) return key;
  }
  return action;
}

const TRINITY_API_URL = 'https://trinitymedia.ai/player/trinity/';

/** Trinity `documentLoadType` query values (publisher documentation). */
export type TrinityDocumentLoadType =
  | 'default'
  | 'onload'
  | 'onSelectorVisible'
  | 'onSelectorExists';

/**
 * Selector Trinity waits on when using `onSelectorVisible` / `onSelectorExists`.
 * Article and briefing bodies mount the player inside this wrapper.
 */
export const TRINITY_CLIPPERABLE_CONTENT_SELECTOR = '.clipperable-content';

/**
 * Base64-encodes a CSS selector for Trinity's `documentLoadTypeSelector` query param (UTF-8 safe).
 * Pass the return value to `URLSearchParams.set` only — it applies query encoding.
 */
export function encodeTrinityDocumentLoadTypeSelector(cssSelector: string): string {
  const trimmed = cssSelector.trim();
  if (!trimmed) return '';
  try {
    return btoa(unescape(encodeURIComponent(trimmed)));
  } catch {
    return '';
  }
}

function applyTrinityDocumentLoadParams(
  url: URL,
  documentLoadType: TrinityDocumentLoadType,
  documentLoadTypeSelector?: string
): void {
  if (documentLoadType === 'default') return;

  if (documentLoadType === 'onload') {
    url.searchParams.set('documentLoadType', 'onload');
    return;
  }

  const encoded = encodeTrinityDocumentLoadTypeSelector(documentLoadTypeSelector ?? '');
  if (!encoded) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        'TrinityAudioPlayer: documentLoadTypeSelector is required when documentLoadType is onSelectorVisible or onSelectorExists'
      );
    }
    return;
  }

  url.searchParams.set('documentLoadType', documentLoadType);
  url.searchParams.set('documentLoadTypeSelector', encoded);
}

const PLAY_CLICKED_TEXT = 'playClicked';
const PAUSE_CLICKED_TEXT = 'pauseClicked';
const PLAYER_READY_TEXT = 'playerReady';
const COMPLETE_TEXT = 'complete';
const RESUME_TEXT = 'resumed';
const ON_FIRST_QUARTILE_TEXT = 'onFirstQuartile';
const ON_MID_POINT_TEXT = 'onMidPoint';
const ON_THIRD_QUARTILE_TEXT = 'onThirdQuartile';

type TrinityAudioPlayerProps = {
  /**
   * Called when Trinity posts a TTS message. Use `track` to limit which logical events invoke this.
   * Compare `detail.event` to `TrinityAudioEventName` or inspect `detail.action` (SDK constants).
   */
  onTrinityEvent?: (detail: TrinityAudioEventDetail) => void;
  /**
   * If set, `onTrinityEvent` runs only for these events (after `window.TRINITY_PLAYER.message` is available).
   * Omit to receive every Trinity TTS message for this page.
   */
  track?: readonly TrinityAudioEventName[];
  /**
   * When true (default), the Trinity script is injected only after `window` `load` so the document and
   * its subresources are ready. Set false to inject as soon as the component mounts (e.g. tests).
   * The player shell is omitted until client hydration (`useServerSideRendering`); `load` runs after that.
   * Client-fetched article HTML may still arrive after `load`; gate rendering this component on data if needed.
   */
  injectAfterPageLoad?: boolean;
  /**
   * Trinity `documentLoadType`: when the player reads the page relative to load / a CSS selector.
   * Use `onSelectorVisible` or `onSelectorExists` with dynamic (AJAX) body text.
   */
  documentLoadType?: TrinityDocumentLoadType;
  /**
   * CSS selector for `onSelectorVisible` / `onSelectorExists`. Omit for `default` / `onload`.
   */
  documentLoadTypeSelector?: string;
};

const TrinityAudioPlayer = ({
  onTrinityEvent,
  track,
  injectAfterPageLoad = true,
  documentLoadType = 'default',
  documentLoadTypeSelector,
}: TrinityAudioPlayerProps) => {
  const hydrated = useServerSideRendering();
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.TrinityAudio,
  });
  const containerRef = useRef(null);
  const onTrinityEventRef = useRef(onTrinityEvent);
  const trackRef = useRef(track);
  const trackEventRef = useRef(trackEvent);

  onTrinityEventRef.current = onTrinityEvent;
  trackRef.current = track;
  trackEventRef.current = trackEvent;

  useEffect(() => {
    if (!hydrated) return undefined;

    const container = containerRef.current;
    if (!container) return undefined;

    let cancelled = false;
    let script: HTMLScriptElement | null = null;

    const inject = () => {
      if (cancelled) return;
      const el = document.createElement('script');
      el.setAttribute('fetchpriority', 'high');
      el.setAttribute('charset', 'UTF-8');

      const url = new URL(`${TRINITY_API_URL}${TRINITY_PLAYER_API_KEY}/`);
      url.searchParams.set('pageURL', window.location.href);
      applyTrinityDocumentLoadParams(url, documentLoadType, documentLoadTypeSelector);
      el.src = url.toString();

      container.appendChild(el);
      script = el;
    };

    const runWhenReady = () => {
      if (!injectAfterPageLoad || document.readyState === 'complete') {
        inject();
        return undefined;
      }
      const onWindowLoad = () => inject();
      window.addEventListener('load', onWindowLoad, { once: true });
      return () => window.removeEventListener('load', onWindowLoad);
    };

    const removeLoadListener = runWhenReady();

    return () => {
      cancelled = true;
      removeLoadListener?.();
      if (script?.parentNode === container) {
        container.removeChild(script);
      }
    };
  }, [hydrated, injectAfterPageLoad, documentLoadType, documentLoadTypeSelector]);

  useEffect(() => {
    if (!hydrated) return undefined;

    const onMessage = (event: MessageEvent) => {
      const trinity = getTrinityPlayer();
      const messageType = trinity?.constants?.postMessageType ?? ('TRINITY_TTS' as const);
      if (event.data?.type !== messageType) return;

      const value = event.data?.value as
        | { action?: string; message?: unknown; playerId?: string }
        | undefined;
      const action = value?.action;
      if (action == null) return;

      const playerId = value.playerId ?? '';
      const message = value.message;
      const watch = trackRef.current;
      const eventName = resolveEventName(action, trinity?.message);

      if (eventName === PLAY_CLICKED_TEXT) {
        trackEventRef.current(click({ clickTarget: ClickTarget.TrinityAudio, clickText: 'play' }));
      } else if (eventName === RESUME_TEXT) {
        trackEventRef.current(
          click({ clickTarget: ClickTarget.TrinityAudio, clickText: 'resume' })
        );
      } else if (eventName === PAUSE_CLICKED_TEXT) {
        trackEventRef.current(click({ clickTarget: ClickTarget.TrinityAudio, clickText: 'pause' }));
      } else if (eventName === PLAYER_READY_TEXT) {
        trackEventRef.current(
          click({ clickTarget: ClickTarget.TrinityAudio, clickText: 'player_ready' })
        );
      } else if (eventName === ON_FIRST_QUARTILE_TEXT) {
        trackEventRef.current(
          impression({
            impressionElement: ImpressionElement.TrinityAudioPlayer,
            context: '25%_completed',
          })
        );
      } else if (eventName === ON_MID_POINT_TEXT) {
        trackEventRef.current(
          impression({
            impressionElement: ImpressionElement.TrinityAudioPlayer,
            context: '50%_completed',
          })
        );
      } else if (eventName === ON_THIRD_QUARTILE_TEXT) {
        trackEventRef.current(
          impression({
            impressionElement: ImpressionElement.TrinityAudioPlayer,
            context: '75%_completed',
          })
        );
      } else if (eventName === COMPLETE_TEXT) {
        trackEventRef.current(
          impression({
            impressionElement: ImpressionElement.TrinityAudioPlayer,
            context: 'completed',
          })
        );
      }

      if (watch?.length) {
        if (action === 'injectorImp') {
          if (!watch.includes('injectorImp')) return;
        } else if (trinity?.message) {
          const allowed = new Set(
            watch
              .filter((n) => n !== 'injectorImp')
              .map((n) => trinity.message![n])
              .filter((v): v is string => Boolean(v))
          );
          if (!allowed.has(action)) return;
        } else {
          return;
        }
      }

      onTrinityEventRef.current?.({
        event: eventName,
        action,
        playerId,
        message,
      });
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [hydrated]);

  if (!hydrated) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="mb-56 max-w-full rounded-20 border border-solid border-grayscale-charcoal p-8 sm:max-w-[500px] dark:bg-white"
    >
      <div
        className="trinity-tts-pb dark:text-black"
        dir="ltr"
        style={{
          font: '12px / 18px Verdana, Arial',
          height: '80px',
          lineHeight: '80px',
          textAlign: 'left',
          margin: '0 0 0 82px',
        }}
      >
        <strong style={{ fontWeight: 400 }}>
          Getting your{' '}
          <a
            href="//trinityaudio.ai"
            style={{
              color: '#4b4a4a',
              textDecoration: 'none',
              fontWeight: 700,
            }}
          >
            Trinity Audio
          </a>{' '}
          player ready…
        </strong>
      </div>
    </div>
  );
};

export default TrinityAudioPlayer;
